import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  MenuBook as BookIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useRecipes } from '../context/RecipeContext';
import { AgentProgressEvent } from '../types/agentTypes';
import { ROUTES } from '../utils/constants';
import { PAGE_NUMBERS } from '../utils/pageNumbers';
import { BookContainer, BookPage, BookNavigation } from '../components';
import OttoThinking from '../components/OttoThinking';
import AIChatDialog from '../components/AIChatDialog';
import { Recipe } from '../models/Recipe';
import imageService from '../services/imageService';
import { Timestamp } from 'firebase/firestore';

const RecipeCaptureScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { addRecipe, updateRecipe, getRecipeById } = useRecipes();
  const { id: recipeId } = useParams<{ id: string }>();
  const isEditMode = !!recipeId;

  const [title, setTitle] = useState<string>('');
  const [ingredients, setIngredients] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [extracting, setExtracting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<AgentProgressEvent | null>(null);
  const [existingRecipe, setExistingRecipe] = useState<Recipe | null>(null);
  const [loadingRecipe, setLoadingRecipe] = useState<boolean>(false);
  const [aiChatOpen, setAiChatOpen] = useState<boolean>(false);

  // Load existing recipe when in edit mode
  useEffect(() => {
    const loadRecipe = async () => {
      if (isEditMode && recipeId) {
        try {
          setLoadingRecipe(true);
          const recipe = await getRecipeById(recipeId);
          if (recipe) {
            setExistingRecipe(recipe);
            // Pre-fill form fields
            setTitle(recipe.title);
            setIngredients(recipe.ingredients);
            setNotes(recipe.notes || '');
            if (recipe.image) {
              setImagePreview(recipe.image);
            }
          } else {
            setError('Recipe not found');
            navigate(ROUTES.RECIPES);
          }
        } catch (err) {
          console.error('Error loading recipe:', err);
          setError('Failed to load recipe');
          navigate(ROUTES.RECIPES);
        } finally {
          setLoadingRecipe(false);
        }
      }
    };

    loadRecipe();
  }, [isEditMode, recipeId, getRecipeById, navigate]);

  const handleBack = () => {
    navigate(ROUTES.RECIPES);
  };

  const handleCaptureImage = async () => {
    try {
      const fileInput = imageService.createFileInput();
      const capturedImage = await imageService.captureImage(fileInput);
      
      if (capturedImage) {
        // Resize image before processing
        const resizedImage = await imageService.resizeImage(capturedImage);
        setImage(resizedImage);
        setImagePreview(URL.createObjectURL(resizedImage));
      }
    } catch (err) {
      console.error('Error capturing image:', err);
      setError('Failed to capture image. Please try again.');
    }
  };

  const handleExtractRecipe = async () => {
    if (!image) {
      setError('Please capture an image first');
      return;
    }

    try {
      setExtracting(true);
      setError(null);
      
      const extractedRecipe = await imageService.processImageWithGPT(
        image,
        `Extract the recipe from this image.

CRITICAL INSTRUCTIONS:
- If this image contains a written recipe (from a cookbook, website, card, or handwritten notes), copy it EXACTLY as written. Do NOT add, remove, or modify any ingredients or instructions.
- Only if the image shows just food/dish photos without a recipe, then create a recipe inspired by what you see.
- Preserve all original measurements, ingredient names, and instructions word-for-word when extracting from a written recipe.

Return it as valid JSON with the following structure:
{
  "title": "Recipe name (exactly as written, or creative name if no recipe visible)",
  "ingredients": "List ingredients exactly as written, separated by newlines. If no written recipe, list key ingredients you see",
  "notes": "Copy any special instructions or cooking notes exactly as written. If no written recipe, provide brief cooking guidance"
}

Return ONLY the JSON object, no markdown formatting or additional text.`
      );
      
      // Try to parse as JSON first
      try {
        // Remove markdown code blocks if present
        const cleanedJson = extractedRecipe
          .replace(/```json\n?/gi, '')
          .replace(/```\n?/g, '')
          .trim();
        
        const recipeData = JSON.parse(cleanedJson);
        setTitle(recipeData.title || '');
        setIngredients(recipeData.ingredients || '');
        setNotes(recipeData.notes || '');
      } catch (parseError) {
        console.error('Error parsing as JSON, trying markdown extraction:', parseError);
        
        // Fallback: Extract from markdown format
        // Look for title (could be # Title or **Title** or Title:)
        const titleMatch = extractedRecipe.match(/(?:^|\n)(?:#\s*|##\s*|\*\*)?(?:title|recipe name|name)[:\s]*([^\n#*]+)/i) ||
                          extractedRecipe.match(/^#\s*([^\n]+)/m) ||
                          extractedRecipe.match(/\*\*([^*]+)\*\*/);
        
        // Look for ingredients section
        const ingredientsMatch = extractedRecipe.match(/(?:ingredients?|items)[:\s]*\n((?:[•\-*]\s*[^\n]+\n?)+)/i) ||
                                extractedRecipe.match(/(?:ingredients?)[:\s]*\n((?:(?:\d+\.|\-)\s*[^\n]+\n?)+)/i);
        
        // Look for instructions/notes
        const notesMatch = extractedRecipe.match(/(?:instructions?|directions?|notes?|method|steps)[:\s]*\n((?:[•\-*\d]\s*[^\n]+\n?)+)/i) ||
                          extractedRecipe.match(/(?:instructions?|directions?|notes?)[:\s]*([^#]+?)(?=\n#|\n\*\*|$)/i);
        
        if (titleMatch) {
          setTitle(titleMatch[1].trim().replace(/[*#]/g, ''));
        }
        
        if (ingredientsMatch) {
          // Clean up bullet points and format
          const cleanIngredients = ingredientsMatch[1]
            .replace(/[•\-*]\s*/g, '')
            .split('\n')
            .filter(line => line.trim())
            .join('\n');
          setIngredients(cleanIngredients);
        }
        
        if (notesMatch) {
          // Clean up formatting
          const cleanNotes = notesMatch[1]
            .replace(/[•\-*]\s*/g, '')
            .replace(/\d+\.\s*/g, '')
            .split('\n')
            .filter(line => line.trim())
            .join('\n');
          setNotes(cleanNotes);
        }
        
        // If we couldn't extract structured data, just put everything in notes
        if (!titleMatch && !ingredientsMatch && !notesMatch) {
          setNotes(extractedRecipe);
        }
      }
    } catch (err) {
      console.error('Error extracting recipe:', err);
      setError('Failed to extract recipe from image. Please try again or enter details manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handleClearImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImage(null);
    setImagePreview(null);
  };

  const handleApplyAIRevisions = async (modifiedRecipe: Recipe) => {
    // Apply AI suggestions to form fields
    setTitle(modifiedRecipe.title);
    setIngredients(modifiedRecipe.ingredients);
    setNotes(modifiedRecipe.notes || '');

    // Update existing recipe reference in edit mode
    if (isEditMode && existingRecipe) {
      setExistingRecipe(modifiedRecipe);
    }

    setAiChatOpen(false);
  };

  const handleSaveRecipe = async () => {
    if (!title && !ingredients && !notes && !image) {
      setError('Please provide at least a recipe title');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode && existingRecipe) {
        // EDIT MODE: Update existing recipe
        const updatedRecipe: Recipe = {
          ...existingRecipe,
          title: title || existingRecipe.title,
          ingredients: ingredients || existingRecipe.ingredients,
          notes: notes || existingRecipe.notes,
          image: imagePreview || existingRecipe.image,
        };

        await updateRecipe(updatedRecipe);
        navigate(`/recipes/${existingRecipe.id}`);
      } else {
        // CREATE MODE: existing logic
        let extractedData: { title?: string; ingredients?: string; notes?: string } | null = null;
        if (image) {
          try {
            const prompt = `Extract the recipe details from this image. Return a JSON object with 'title', 'ingredients' (newline-separated string), and 'notes' fields.`;
            const result = await imageService.processImageWithGPT(image, prompt);
            const cleanedJson = result.replace(/```json\n?/gi, '').replace(/```\n?/g, '').trim();
            extractedData = JSON.parse(cleanedJson);
          } catch (err) {
            console.warn('Image extraction failed:', err);
          }
        }

        const newRecipe = await addRecipe(
          {
            title: title || extractedData?.title || 'Untitled Recipe',
            ingredients: ingredients || extractedData?.ingredients || '',
            notes: notes || extractedData?.notes || '',
            image: imagePreview || undefined,
          },
          (event) => setProgress(event)
        );

        navigate(`/recipes/${newRecipe.id}`);
      }
    } catch (err) {
      console.error('Failed to save recipe:', err);
      setError(isEditMode ? 'Failed to update recipe' : 'Failed to create recipe');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  if (loadingRecipe) {
    return (
      <BookContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      </BookContainer>
    );
  }

  return (
    <BookContainer>
      {/* Two-page book spread - Single column on mobile */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: 0,
        maxWidth: { xs: '100%', md: 1200 },
        margin: '0 auto',
        height: { xs: 'auto', md: 'calc(100vh - 40px)' },
        maxHeight: { xs: 'none', md: '900px' },
        minHeight: { xs: '100vh', md: 'auto' },
      }}>
          {/* Left Page - Recipe Photo, Title, and Ingredients */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: 'auto', md: '100%' },
            minHeight: { xs: '50vh', md: 'auto' },
          }}>
            <BookPage isLeftPage={true} pageNumber={PAGE_NUMBERS.RECIPE_CAPTURE}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' } as any}
                >
                  {/* Page Header - Fixed */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                      <IconButton
                        onClick={handleBack}
                        sx={{
                          mr: { xs: 1, md: 2 },
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.8)'
                            : 'rgba(139, 69, 19, 0.8)',
                          p: { xs: 0.5, md: 1 },
                        }}
                      >
                        <ArrowBackIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                      </IconButton>
                      <BookIcon
                        sx={{
                          fontSize: { xs: 20, md: 28 },
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.8)'
                            : 'rgba(139, 69, 19, 0.8)',
                          mr: { xs: 1, md: 2 },
                        }}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: '"Playfair Display", serif',
                          fontWeight: 700,
                          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.9)'
                            : 'rgba(139, 69, 19, 0.9)',
                        }}
                      >
                        {isEditMode ? 'Edit Recipe' : 'Add New Recipe'}
                      </Typography>
                    </Box>

                    {/* Decorative line */}
                    <Box sx={{ 
                      height: 3,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, rgba(255, 138, 101, 0.3), transparent)'
                        : 'linear-gradient(90deg, rgba(139, 69, 19, 0.3), transparent)',
                      mb: 2,
                    }} />

                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}
                  </Box>

                  {/* Scrollable Content Container */}
                  <Box sx={{
                    flex: 1,
                    overflowY: { xs: 'visible', md: 'auto' },
                    pr: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                      background: theme.palette.mode === 'dark'
                        ? 'rgba(255, 248, 240, 0.2)'
                        : 'rgba(139, 69, 19, 0.2)',
                      borderRadius: '4px'
                    },
                  }}>
                    {/* Recipe Title */}
                    <Box sx={{ mb: 3 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontFamily: '"Playfair Display", serif',
                          fontWeight: 600,
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.9)' 
                            : 'rgba(139, 69, 19, 0.9)',
                          mb: 1,
                          borderBottom: `1px dotted ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 248, 240, 0.3)' 
                            : 'rgba(139, 69, 19, 0.3)'}`,
                          pb: 1,
                        }}
                      >
                        Recipe Name
                      </Typography>
                      <TextField
                        fullWidth
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter your recipe title..."
                        variant="standard"
                        required
                        sx={{
                          '& .MuiInput-root': {
                            fontFamily: '"Crimson Text", serif',
                            fontSize: '1.2rem',
                            '&:before': {
                              borderBottom: `1px dotted ${theme.palette.mode === 'dark' 
                                ? 'rgba(255, 248, 240, 0.3)' 
                                : 'rgba(139, 69, 19, 0.3)'}`,
                            },
                            '&:after': {
                              borderBottom: `2px solid ${theme.palette.mode === 'dark' 
                                ? 'rgba(255, 138, 101, 0.8)' 
                                : 'rgba(139, 69, 19, 0.8)'}`,
                            },
                          },
                          '& .MuiInput-input': {
                            color: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 248, 240, 0.9)' 
                              : 'rgba(139, 69, 19, 0.9)',
                          },
                        }}
                      />
                    </Box>

                    {/* Photo Section */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: '"Playfair Display", serif',
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.9)'
                            : 'rgba(139, 69, 19, 0.9)',
                          mb: 1,
                          borderBottom: `1px dotted ${theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.3)'
                            : 'rgba(139, 69, 19, 0.3)'}`,
                          pb: 1,
                        }}
                      >
                        Recipe Photo
                      </Typography>

                      {imagePreview ? (
                        <Box sx={{ position: 'relative', mb: 2 }}>
                          <Box
                            sx={{
                              width: '100%',
                              maxWidth: { xs: '100%', md: 300 },
                              height: { xs: 180, md: 200 },
                              border: `2px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255, 248, 240, 0.3)'
                                : 'rgba(139, 69, 19, 0.3)'}`,
                              borderRadius: '8px',
                              overflow: 'hidden',
                              position: 'relative',
                            }}
                          >
                            <img
                              src={imagePreview}
                              alt="Recipe"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                },
                              }}
                              onClick={handleClearImage}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: { xs: '100%', md: 300 },
                            height: { xs: 120, md: 150 },
                            border: `2px dashed ${theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.3)'
                              : 'rgba(139, 69, 19, 0.3)'}`,
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 2,
                            bgcolor: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.02)'
                              : 'rgba(139, 69, 19, 0.05)',
                          }}
                        >
                          <CameraIcon
                            sx={{
                              fontSize: { xs: 32, md: 40 },
                              color: theme.palette.mode === 'dark'
                                ? 'rgba(255, 248, 240, 0.4)'
                                : 'rgba(139, 69, 19, 0.4)',
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.mode === 'dark'
                                ? 'rgba(255, 248, 240, 0.6)'
                                : 'rgba(139, 69, 19, 0.6)',
                              fontFamily: '"Crimson Text", serif',
                              fontSize: { xs: '0.875rem', md: '1rem' },
                            }}
                          >
                            Photo will appear here
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<CameraIcon />}
                          onClick={handleCaptureImage}
                          size="small"
                          sx={{
                            fontFamily: '"Crimson Text", serif',
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            width: { xs: '100%', sm: 'auto' },
                            border: `1px solid ${theme.palette.mode === 'dark'
                              ? 'rgba(255, 138, 101, 0.5)'
                              : 'rgba(139, 69, 19, 0.5)'}`,
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.8)'
                              : 'rgba(139, 69, 19, 0.8)',
                          }}
                        >
                          Add Photo
                        </Button>
                        <Button
                          variant="text"
                          onClick={handleExtractRecipe}
                          disabled={!image || extracting}
                          size="small"
                          sx={{
                            fontFamily: '"Crimson Text", serif',
                            fontSize: { xs: '0.875rem', md: '1rem' },
                            width: { xs: '100%', sm: 'auto' },
                            color: theme.palette.mode === 'dark'
                              ? 'rgba(255, 138, 101, 0.8)'
                              : 'rgba(139, 69, 19, 0.8)',
                          }}
                        >
                          {extracting ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              Extracting...
                            </>
                          ) : (
                            'Extract from Photo'
                          )}
                        </Button>
                      </Box>
                    </Box>

                    {/* Ingredients Section (Moved from Right Page) */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: '"Playfair Display", serif',
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.9)'
                            : 'rgba(139, 69, 19, 0.9)',
                          mb: 1,
                          borderBottom: `1px dotted ${theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.3)'
                            : 'rgba(139, 69, 19, 0.3)'}`,
                          pb: 1,
                        }}
                      >
                        Ingredients
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        value={ingredients}
                        onChange={(e) => setIngredients(e.target.value)}
                        placeholder="List your ingredients here, one per line..."
                        variant="standard"
                        sx={{
                          '& .MuiInput-root': {
                            fontFamily: '"Crimson Text", serif',
                            fontSize: '1rem',
                            lineHeight: 1.8,
                            '&:before': {
                              borderBottom: 'none',
                            },
                            '&:after': {
                              borderBottom: 'none',
                            },
                          },
                          '& .MuiInput-input': {
                            color: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 248, 240, 0.8)' 
                              : 'rgba(139, 69, 19, 0.8)',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </motion.div>
              </Box>
            </BookPage>
          </Box>

          {/* Right Page - Instructions and Save */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            height: { xs: 'auto', md: '100%' },
            minHeight: { xs: '50vh', md: 'auto' },
          }}>
            <BookPage isLeftPage={false} pageNumber={PAGE_NUMBERS.RECIPE_CAPTURE + 1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  style={{ height: '100%', display: 'flex', flexDirection: 'column' } as any}
                >
                  {/* Instructions Section */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: '300px', md: 0 },
                  }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 600,
                        fontSize: { xs: '1rem', md: '1.25rem' },
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.9)'
                          : 'rgba(139, 69, 19, 0.9)',
                        mb: 2,
                        borderBottom: `1px dotted ${theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.3)'
                          : 'rgba(139, 69, 19, 0.3)'}`,
                        pb: 1,
                      }}
                    >
                      Notes & Instructions
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Write your cooking instructions and notes here..."
                      variant="standard"
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        '& .MuiInputBase-root': {
                          height: '100%',
                          alignItems: 'flex-start',
                          fontFamily: '"Crimson Text", serif',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          lineHeight: 1.8,
                          '&:before': {
                            borderBottom: 'none',
                          },
                          '&:after': {
                            borderBottom: 'none',
                          },
                        },
                        '& .MuiInput-input': {
                          height: '100% !important',
                          overflow: 'auto !important',
                          color: theme.palette.mode === 'dark'
                            ? 'rgba(255, 248, 240, 0.8)'
                            : 'rgba(139, 69, 19, 0.8)',
                          '&::-webkit-scrollbar': { width: '4px' },
                          '&::-webkit-scrollbar-track': { background: 'transparent' },
                          '&::-webkit-scrollbar-thumb': {
                            background: theme.palette.mode === 'dark'
                              ? 'rgba(255, 248, 240, 0.2)'
                              : 'rgba(139, 69, 19, 0.2)',
                            borderRadius: '4px'
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Ask Otto for Help Button */}
                  <Box sx={{ mt: 4, mb: 3 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<HelpOutlineIcon />}
                      onClick={() => setAiChatOpen(true)}
                      sx={{
                        fontSize: '1.2rem',
                        py: 2,
                        backgroundColor: 'secondary.main',
                        color: 'white',
                        boxShadow: 3,
                        fontFamily: '"Crimson Text", serif',
                      }}
                    >
                      Ask Otto for Help
                    </Button>
                  </Box>

                  {/* Save Button - Fixed at bottom */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    pt: { xs: 2, md: 3 },
                    borderTop: `1px dotted ${theme.palette.mode === 'dark'
                      ? 'rgba(255, 248, 240, 0.3)'
                      : 'rgba(139, 69, 19, 0.3)'}`,
                  }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveRecipe}
                      disabled={loading || !title}
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      sx={{
                        fontFamily: '"Crimson Text", serif',
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        width: { xs: '100%', sm: 'auto' },
                        px: { xs: 2, md: 4 },
                        py: { xs: 1.2, md: 1.5 },
                        background: theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.3)'
                          : 'rgba(139, 69, 19, 0.8)',
                        color: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.9)'
                          : 'rgba(255, 248, 240, 0.95)',
                        border: `2px solid ${theme.palette.mode === 'dark'
                          ? 'rgba(255, 138, 101, 0.5)'
                          : 'rgba(139, 69, 19, 0.6)'}`,
                        borderRadius: '8px',
                        textTransform: 'none',
                        '&:hover': {
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255, 138, 101, 0.4)'
                            : 'rgba(139, 69, 19, 0.9)',
                        },
                      }}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                          {isEditMode ? 'Updating Recipe...' : 'Adding Recipe...'}
                        </>
                      ) : (
                        isEditMode ? 'Update Recipe' : 'Add to Recipe Book'
                      )}
                    </Button>
                  </Box>
                </motion.div>
              </Box>
            </BookPage>
          </Box>
        </Box>
      
      {/* Book Navigation */}
      <BookNavigation currentPageNumber={PAGE_NUMBERS.RECIPE_CAPTURE} />

      {/* Otto thinking animation overlay */}
      <OttoThinking progress={loading && progress ? progress : undefined} size={80} />

      {/* AI Chat Dialog */}
      <AIChatDialog
        open={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        context={{
          recipe: existingRecipe || {
            id: 'draft',
            title: title,
            ingredients: ingredients,
            notes: notes,
            userId: '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            isFavorite: false,
          } as Recipe
        }}
        onApplyRevisions={handleApplyAIRevisions}
      />
    </BookContainer>
  );
};

export default RecipeCaptureScreen;
